import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Card, TextField, InputAdornment, Typography,
  Chip, Select, MenuItem, FormControl, InputLabel, Stack, Alert, Button,
  useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import { CreditEditDialog } from "../components/CreditEditDialog.js";
import { useApi } from "../hooks/useApi.js";
import { ui } from "../store/uiStore.js";
import { currentPeriod, type Credit, type PartnerReduced } from "@kappa-v2/contracts";

// I periodi reali sono in formato "MM/YYYY" → nessuna trasformazione necessaria.
const fmtPeriod = (p: string) => p;
// Valore ordinabile cronologico (YYYY*100 + MM) per ordinare dal più recente al più vecchio.
const periodRank = (period: string) => {
  const [mm, yyyy] = period.split("/");
  return (Number(yyyy) || 0) * 100 + (Number(mm) || 0);
};

export function CreditsPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const personId = searchParams.get("personId");

  const [rows, setRows] = useState<Credit[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Se arrivo dal CTA di un socio mostro tutti i periodi; altrimenti il periodo corrente.
  const [period, setPeriod] = useState(personId ? "" : currentPeriod());
  const [editCredit, setEditCredit] = useState<Credit | null>(null);
  const [deleteCredit, setDeleteCredit] = useState<Credit | null>(null);

  const handleDeleteCredit = useCallback(async () => {
    if (!deleteCredit?._id) return;
    try {
      await call(`/api/v1/payments/credits/${deleteCredit._id}`, { method: "DELETE" });
      ui.toast(t("common.deleted"), "success");
    } catch {
      ui.toast(t("common.error"), "error");
    }
    setDeleteCredit(null);
    fetchAll();
  }, [deleteCredit, call, t]);

  const fetchAll = useCallback(() => {
    setLoading(true);
    call<{ data: Credit[] } | Credit[]>("/api/v1/payments/credits")
      .then(r => setRows(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Anagrafica ridotta per risolvere personId → nominativo socio.
  useEffect(() => {
    call<{ data?: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all", { silent: true, noLoader: true })
      .then((r) => {
        const list = Array.isArray(r) ? r : (r.data ?? []);
        setNames(Object.fromEntries(list.map((p) => [String(p._id), p.fullName])));
      })
      .catch(() => setNames({}));
  }, [call]);

  const periods = [...new Set(rows.map(r => r.period))].sort((a, b) => periodRank(b) - periodRank(a));

  const filtered = rows
    .filter(r => !personId || r.personId === personId)
    .filter(r => !period || r.period === period)
    .filter(r => `${names[r.personId] ?? ""} ${r.name} ${r.description}`.toLowerCase().includes(search.toLowerCase()))
    // Dal più nuovo al più vecchio: per periodo decrescente, poi per data di inserimento.
    .sort((a, b) => {
      const d = periodRank(b.period) - periodRank(a.period);
      if (d !== 0) return d;
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta || String(b._id ?? "").localeCompare(String(a._id ?? ""));
    });

  const totalFiltered = filtered.reduce((s, r) => s + r.credit, 0);

  const columns: GridColDef[] = [
    {
      field: "socio", headerName: t("credits.partner"), flex: 1, minWidth: 180,
      valueGetter: (_v, row: Credit) => names[row.personId] ?? row.personId,
      renderCell: (params: GridRenderCellParams<Credit>) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {names[params.row.personId] || params.row.personId}
        </Typography>
      )
    },
    {
      field: "name", headerName: t("common.description"), flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams<Credit>) => (
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography variant="body2" fontWeight={600} noWrap title={params.row.name}>{params.row.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div" title={params.row.description}>
            {params.row.description}
          </Typography>
        </Box>
      )
    },
    {
      field: "period", headerName: t("common.period"), width: 110,
      renderCell: (params: GridRenderCellParams<Credit>) => (
        <Chip size="small" label={fmtPeriod(params.row.period)} variant="outlined" sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "credit", headerName: t("common.amount"), width: 120,
      renderCell: (params: GridRenderCellParams<Credit>) => (
        <Typography variant="body2" fontWeight={700} color="warning.dark">
          € {params.row.credit.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: "isDeducible", headerName: t("credits.deductible"), width: 110,
      renderCell: (params: GridRenderCellParams<Credit>) => (
        <Typography variant="caption" color={params.row.isDeducible ? "success.main" : "text.disabled"}>
          {params.row.isDeducible ? t("common.yes") : t("common.no")}
        </Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false, align: "right", headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Credit>) => (
        <RowQuickActions
          ariaLabel={t("common.actions")}
          quickKeys={["edit"]}
          items={[
            { key: "edit", label: t("common.edit"), description: t("credits.editTitle"), icon: <EditIcon fontSize="small" />, onClick: () => setEditCredit(params.row) },
            { key: "del", label: t("common.delete"), description: t("credits.deleteConfirm", { name: params.row.name }), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteCredit(params.row) }
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("credits.title")} subtitle={t("credits.subtitle")} onRefresh={fetchAll} />
      <Box sx={{ p: 3, flex: 1 }}>
        {personId && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/protected/credits")}>
                {t("credits.showAll")}
              </Button>
            }
          >
            {t("credits.filteredSingle")}
          </Alert>
        )}
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" placeholder={t("credits.searchPlaceholder")}
              data-tour="cr-search"
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 260 } }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }} data-tour="cr-period">
              <InputLabel>{t("common.period")}</InputLabel>
              <Select label={t("common.period")} value={period} onChange={e => setPeriod(e.target.value as string)} MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
                <MenuItem value="">{t("common.all")}</MenuItem>
                {periods.map(p => <MenuItem key={p} value={p}>{fmtPeriod(p)}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1} alignItems="center" data-tour="cr-total">
              <Typography variant="caption" color="text.secondary">{t("credits.totalFiltered")}</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="warning.dark">
                € {totalFiltered.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </Typography>
            </Stack>
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("credits.none")} description={t("credits.noneDesc")} />
            : isMobile
              ? (
                <Box>
                  {filtered.map((r, idx) => (
                    <Box key={r._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0 }}>
                          {names[r.personId] || r.personId}
                        </Typography>
                        <Chip size="small" label={fmtPeriod(r.period)} variant="outlined" sx={{ fontSize: "0.72rem", flexShrink: 0 }} />
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{r.description}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={700} color="warning.dark">
                          € {r.credit.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                        </Typography>
                        <Chip size="small" variant="outlined"
                          label={`${t("credits.deductible")}: ${r.isDeducible ? t("common.yes") : t("common.no")}`}
                          color={r.isDeducible ? "success" : "default"} sx={{ fontSize: "0.72rem" }} />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )
              : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                  loading={loading} autoHeight rowHeight={60} pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  disableRowSelectionOnClick sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                    "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                    "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                    "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
                  }}
                />
          }
        </Card>
      </Box>
      <CreditEditDialog credit={editCredit} onClose={() => setEditCredit(null)} onSaved={fetchAll} />
      <ConfirmDialog
        open={Boolean(deleteCredit)}
        message={t("credits.deleteConfirm", { name: deleteCredit?.name })}
        onConfirm={handleDeleteCredit}
        onCancel={() => setDeleteCredit(null)}
      />
    </Box>
  );
}
